import mongoose from "mongoose"
import type { PaginationResponse, PaginationSpec } from "@rpcbase/api"

import type { PaginationCursorCodecOptions } from "./cursor"
import { paginateMongoQuery } from "./paginateMongoQuery"


declare const mongoPaginationPluginBrand: unique symbol


type QueryOptionsLike = Record<string, unknown>

const getQueryOptions = (query: unknown): QueryOptionsLike | undefined => {
  if (!query || typeof query !== "object") return undefined
  if (!("getOptions" in query) || typeof (query as { getOptions?: unknown }).getOptions !== "function") return undefined
  return (query as { getOptions: () => QueryOptionsLike }).getOptions()
}

const getPaginationFromOptions = (query: unknown): PaginationSpec | undefined => {
  const options = getQueryOptions(query)
  return options?.pagination as PaginationSpec | undefined
}

const getCursorFromOptions = (query: unknown): PaginationCursorCodecOptions | undefined => {
  const options = getQueryOptions(query)
  return options?.paginationCursor as PaginationCursorCodecOptions | undefined
}

export type MongoPaginationPluginOptions = {
  cursor: PaginationCursorCodecOptions;
}

type QueryResultNode<TResult> = TResult extends Array<infer TNode> ? TNode : unknown

declare module "mongoose" {
  interface Query<
    ResultType,
    DocType,
    THelpers = {},
    RawDocType = unknown,
    QueryOp = "find",
    TDocOverrides = Record<string, never>,
  > {
    [mongoPaginationPluginBrand]?: [DocType, THelpers, RawDocType, QueryOp, TDocOverrides];
    paginate: (
      pagination?: PaginationSpec,
      options?: MongoPaginationPluginOptions,
    ) => Promise<PaginationResponse<QueryResultNode<ResultType>>>;
  }
}

export const mongoPaginationPlugin = (schema: mongoose.Schema, pluginOptions?: MongoPaginationPluginOptions): void => {
  ;(schema.query as Record<string, unknown>).paginate = async function(
    this: mongoose.Query<unknown[], unknown>,
    pagination?: PaginationSpec,
    options?: MongoPaginationPluginOptions,
  ): Promise<PaginationResponse<unknown>> {
    const spec = pagination ?? getPaginationFromOptions(this)
    if (!spec) throw new Error("Missing pagination spec")

    const cursor = options?.cursor ?? getCursorFromOptions(this) ?? pluginOptions?.cursor
    if (!cursor?.signingSecret) throw new Error("Missing pagination cursor signingSecret")

    return await paginateMongoQuery(this, spec, { cursor })
  }
}
